/**
 * GPN Admin Portal - Full Website Dynamic DOM Content Translator
 * Automatically translates all dynamic and static text nodes across the entire
 * admin portal whenever the user changes the language.
 *
 * - Zero impact on React event listeners or component hierarchy.
 * - Safely skips input fields, textareas, scripts, styles, codes, and passwords.
 * - Restores original English text instantly when switching back to English.
 * - Automatically watches for new data loaded from APIs via MutationObserver.
 */

import { getStaticText } from '../translations/staticTranslations';
import TranslationService from './translationService';

class DomTranslator {
  static globalLanguage = 'en';
  static activeLanguage = 'en';
  static observer = null;
  static isTranslating = false;
  static debounceTimer = null;
  static listenersAttached = false;

  // Elements that must NEVER be translated
  static SKIPPED_TAGS = new Set([
    'SCRIPT',
    'STYLE',
    'NOSCRIPT',
    'INPUT',
    'TEXTAREA',
    'SELECT',
    'OPTION',
    'CODE',
    'PRE',
    'SVG',
    'PATH',
  ]);

  /**
   * Determine effective language for the active route (module override or global)
   */
  static getEffectiveLanguageForRoute() {
    if (typeof window === 'undefined') return this.globalLanguage || 'en';
    // If global language is explicitly set to English, return English everywhere
    if (this.globalLanguage === 'en') return 'en';

    const path = (window.location.pathname || '').toLowerCase();

    const routeToModule = [
      { prefix: '/admin/prayers', keys: ['prayer', 'prayers'] },
      { prefix: '/admin/public-prayers', keys: ['prayer', 'prayers'] },
      { prefix: '/admin/private-prayers', keys: ['private_prayer', 'prayer', 'prayers'] },
      { prefix: '/admin/prayer-closet', keys: ['special_prayers', 'prayer'] },
      { prefix: '/admin/special-prayers', keys: ['special_prayers', 'prayer'] },
      { prefix: '/admin/add-special-prayer', keys: ['special_prayers', 'prayer'] },
      { prefix: '/admin/events', keys: ['events'] },
      { prefix: '/admin/songs', keys: ['songs'] },
      { prefix: '/admin/groups', keys: ['groups'] },
      { prefix: '/admin/organizations', keys: ['organizations'] },
      { prefix: '/admin/testimonies', keys: ['testimony', 'testimonies'] },
      { prefix: '/admin/feedbacks', keys: ['feedbacks'] },
      { prefix: '/admin/drivers', keys: ['ride'] },
      { prefix: '/admin/ride-connect', keys: ['ride'] },
      { prefix: '/admin/share-accept', keys: ['share-accept'] },
      { prefix: '/admin/giver-items', keys: ['giver-items', 'share-accept'] },
    ];

    for (const mapping of routeToModule) {
      if (path.startsWith(mapping.prefix)) {
        for (const k of mapping.keys) {
          const modLang = localStorage.getItem(`gpn_mod_lang_${k}`);
          if (modLang && modLang !== 'default') {
            return modLang.toLowerCase().trim();
          }
        }
      }
    }

    return this.globalLanguage || 'en';
  }

  /**
   * Update active language dynamically for current route/module
   */
  static updateLanguageForRoute() {
    const effectiveLang = this.getEffectiveLanguageForRoute();
    if (effectiveLang !== this.activeLanguage) {
      this.restoreOriginalText();
      this.activeLanguage = effectiveLang;
      if (effectiveLang !== 'en') {
        this.translatePage();
        this.startObserver();
      } else {
        this.stopObserver();
      }
    }
  }

  /**
   * Start or update dynamic full-page translation
   * @param {string} lang Target global language code ('en', 'te', 'hi', etc.)
   */
  static setLanguage(lang) {
    this.globalLanguage = (lang || 'en').toLowerCase().trim();

    // Attach listeners once
    if (!this.listenersAttached && typeof window !== 'undefined') {
      this.listenersAttached = true;
      window.addEventListener('popstate', () => this.updateLanguageForRoute());
      window.addEventListener('gpn_module_language_changed', () => this.updateLanguageForRoute());
    }

    const effectiveLang = this.getEffectiveLanguageForRoute();

    if (effectiveLang === 'en') {
      this.activeLanguage = 'en';
      this.restoreOriginalText();
      this.stopObserver();
      return;
    }

    if (this.activeLanguage === effectiveLang && this.observer) {
      return;
    }

    this.activeLanguage = effectiveLang;

    // Translate current page contents
    this.translatePage();

    // Start watching for dynamic DOM updates (API responses, table paginations, route transitions)
    this.startObserver();
  }

  /**
   * Scans and translates visible text nodes in the DOM
   */
  static translatePage() {
    if (this.activeLanguage === 'en' || typeof document === 'undefined') return;

    const root = document.getElementById('root') || document.body;
    if (!root) return;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        if (!node || !node.parentNode) return NodeFilter.FILTER_REJECT;

        const parent = node.parentNode;
        const tagName = parent.tagName || '';

        // Skip non-translatable tags
        if (this.SKIPPED_TAGS.has(tagName)) return NodeFilter.FILTER_REJECT;

        // Skip elements explicitly marked not to translate or sidebar navigation / drawers
        if (
          parent.isContentEditable ||
          parent.closest?.(
            '[data-no-translate="true"], [translate="no"], .notranslate, nav, [aria-label="sidebar navigation"], .MuiDrawer-root, [data-gpn-translator="true"]'
          ) ||
          parent.classList?.contains('MuiSvgIcon-root')
        ) {
          return NodeFilter.FILTER_REJECT;
        }

        const text = node.nodeValue?.trim();
        // Skip empty, single punctuation, or pure numbers
        if (!text || text.length < 2 || /^[\d\s.,:;!?'"()\-–—/\\+*#@$%^&=]+$/.test(text)) {
          return NodeFilter.FILTER_REJECT;
        }

        return NodeFilter.FILTER_ACCEPT;
      },
    });

    const textNodes = [];
    let currentNode;
    while ((currentNode = walker.nextNode())) {
      textNodes.push(currentNode);
    }

    this.processTextNodes(textNodes);
  }

  /**
   * Processes an array of text nodes for translation
   */
  static async processTextNodes(nodes) {
    if (!nodes || !nodes.length || this.activeLanguage === 'en') return;

    const currentTarget = this.activeLanguage;

    for (const node of nodes) {
      if (!node || !node.parentNode) continue;

      const fullValue = node.nodeValue;
      if (!fullValue) continue;

      const trimmed = fullValue.trim();
      if (!trimmed || trimmed.length < 2) continue;

      // Preserve original English text on the node ONLY if it contains Latin characters
      if (node.__gpn_original_text == null && /[a-zA-Z]/.test(fullValue)) {
        node.__gpn_original_text = fullValue;
      }

      // If already translated for this language, skip
      if (node.__gpn_translated_lang === currentTarget) {
        continue;
      }

      const originalTrimmed = (node.__gpn_original_text || fullValue).trim();

      // 1. Check static dictionary first
      const staticTranslation = getStaticText(currentTarget, originalTrimmed, null);
      if (staticTranslation && staticTranslation !== originalTrimmed) {
        this.applyTranslation(node, staticTranslation, currentTarget);
        continue;
      }

      // 2. Dynamic translation via TranslationService
      TranslationService.translate(originalTrimmed, currentTarget)
        .then((translated) => {
          if (this.activeLanguage === currentTarget && translated && translated !== originalTrimmed) {
            this.applyTranslation(node, translated, currentTarget);
          }
        })
        .catch(() => {});
    }
  }

  /**
   * Safely applies translated text to a text node, preserving whitespace
   */
  static applyTranslation(node, translated, lang) {
    if (!node || !node.parentNode || this.activeLanguage !== lang) return;

    const orig = node.__gpn_original_text || node.nodeValue;
    const matchLeading = orig.match(/^(\s*)/);
    const matchTrailing = orig.match(/(\s*)$/);
    const leading = matchLeading ? matchLeading[1] : '';
    const trailing = matchTrailing ? matchTrailing[1] : '';

    node.__gpn_translated_lang = lang;
    node.nodeValue = `${leading}${translated}${trailing}`;
  }

  /**
   * Restores original English text for all translated nodes
   */
  static restoreOriginalText() {
    if (typeof document === 'undefined') return;

    const root = document.getElementById('root') || document.body;
    if (!root) return;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        if (!node || !node.parentNode) return NodeFilter.FILTER_REJECT;
        const parent = node.parentNode;
        if (
          parent.closest?.(
            '[data-no-translate="true"], [translate="no"], .notranslate, nav, [aria-label="sidebar navigation"], .MuiDrawer-root, [data-gpn-translator="true"]'
          )
        ) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    let node;
    while ((node = walker.nextNode())) {
      if (node.__gpn_original_text != null) {
        if (/[a-zA-Z]/.test(node.__gpn_original_text)) {
          node.nodeValue = node.__gpn_original_text;
        }
        node.__gpn_original_text = null;
        node.__gpn_translated_lang = null;
      }
    }
  }

  /**
   * Start observing DOM mutations for dynamic content changes
   */
  static startObserver() {
    this.stopObserver();

    if (typeof MutationObserver === 'undefined') return;

    const root = document.getElementById('root') || document.body;
    if (!root) return;

    this.observer = new MutationObserver((mutations) => {
      if (this.activeLanguage === 'en') return;

      // Debounce mutation handling to avoid layout thrashing
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.translatePage();
      }, 150);
    });

    this.observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  /**
   * Stop MutationObserver
   */
  static stopObserver() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    clearTimeout(this.debounceTimer);
  }
}

export default DomTranslator;
